/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
/**
 * LobeHub Icon Loader
 * Dynamically load and render icons from @lobehub/icons
 *
 * Supports:
 * - Basic: "OpenAI", "OpenAI.Color"
 * - Chained properties: "OpenAI.Avatar.type={'platform'}"
 * - Size parameter: getLobeIcon("OpenAI", 20)
 */
import { useEffect, useRef, useState } from 'react'

// ── 惰性加载 @lobehub/icons ────────────────────────────────────────────────
// 该库打包后 ~4.7MB(数千个 SVG 组件),静态整库导入会让所有引用页(含首页
// 模型市场)一进来就下载全量图标,是 PageSpeed「未使用的 JS」的最大来源。
// 改为:图标元素进入视口时才 import() 一次,全局共享;加载前渲染首字母占位。
type IconsModule = Record<string, unknown>
let loadedIcons: IconsModule | null = null
let loadingPromise: Promise<void> | null = null
const subscribers = new Set<() => void>()

function ensureIconsLoaded(): void {
  if (loadedIcons || loadingPromise) return
  loadingPromise = import('@lobehub/icons')
    .then((m) => {
      loadedIcons = m as unknown as IconsModule
      subscribers.forEach((cb) => cb())
      subscribers.clear()
    })
    .catch(() => {
      loadingPromise = null // 失败允许下次重试
    })
}

/**
 * Parse a property value from string to appropriate type
 * @param raw - Raw string value
 * @returns Parsed value (boolean, number, or string)
 */
function parseValue(raw: string | undefined | null): string | number | boolean {
  if (raw == null) return true

  let v = String(raw).trim()

  // Remove curly braces
  if (v.startsWith('{') && v.endsWith('}')) {
    v = v.slice(1, -1).trim()
  }

  // Remove quotes
  if (
    (v.startsWith('"') && v.endsWith('"')) ||
    (v.startsWith("'") && v.endsWith("'"))
  ) {
    return v.slice(1, -1)
  }

  // Boolean
  if (v === 'true') return true
  if (v === 'false') return false

  // Number
  if (/^-?\d+(?:\.\d+)?$/.test(v)) return Number(v)

  // Return as string
  return v
}

/**
 * Get LobeHub icon component by name
 * @param iconName - Icon name/description (e.g., "OpenAI", "OpenAI.Color", "Claude.Avatar")
 * @param size - Icon size (default: 20)
 * @returns Icon component or fallback
 *
 * @example
 * getLobeIcon("OpenAI", 24)
 * getLobeIcon("OpenAI.Color", 20)
 * getLobeIcon("Claude.Avatar.type={'platform'}", 32)
 */
export function getLobeIcon(
  iconName: string | undefined | null,
  size: number = 20
): React.ReactNode {
  if (!iconName || typeof iconName !== 'string') {
    return (
      <div
        className='bg-muted text-muted-foreground flex items-center justify-center rounded-full text-xs font-medium'
        style={{ width: size, height: size }}
      >
        ?
      </div>
    )
  }

  const trimmedName = iconName.trim()
  if (!trimmedName) {
    return (
      <div
        className='bg-muted text-muted-foreground flex items-center justify-center rounded-full text-xs font-medium'
        style={{ width: size, height: size }}
      >
        ?
      </div>
    )
  }

  return <LazyLobeIcon name={trimmedName} size={size} />
}

/** 首字母占位(库未加载/图标不存在时) */
function LetterBadge({ name, size }: { name: string; size: number }) {
  return (
    <div
      className='bg-muted text-muted-foreground flex items-center justify-center rounded-full text-xs font-medium'
      style={{ width: size, height: size }}
    >
      {name.charAt(0).toUpperCase()}
    </div>
  )
}

function LazyLobeIcon({ name, size }: { name: string; size: number }) {
  const ref = useRef<HTMLSpanElement>(null)
  const [, force] = useState(0)

  useEffect(() => {
    if (loadedIcons) return
    const rerender = () => force((n) => n + 1)
    subscribers.add(rerender)
    // 进入视口才触发整库加载:首页折叠下方的图标不会在首屏跑分期间拉 4.7MB
    const el = ref.current
    let io: IntersectionObserver | undefined
    if (el && 'IntersectionObserver' in window) {
      io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          ensureIconsLoaded()
          io?.disconnect()
        }
      })
      io.observe(el)
    } else {
      ensureIconsLoaded()
    }
    return () => {
      subscribers.delete(rerender)
      io?.disconnect()
    }
  }, [])

  if (!loadedIcons) {
    return (
      <span ref={ref} className='inline-flex'>
        <LetterBadge name={name} size={size} />
      </span>
    )
  }
  return <>{renderLoadedIcon(loadedIcons, name, size)}</>
}

function renderLoadedIcon(
  LobeIcons: IconsModule,
  trimmedName: string,
  size: number
): React.ReactNode {
  // Parse component path and chained properties
  const segments = trimmedName.split('.')
  const baseKey = segments[0]
  const BaseIcon = (LobeIcons as Record<string, unknown>)[baseKey] as
    | Record<string, unknown>
    | undefined

  let IconComponent: React.ComponentType<Record<string, unknown>> | undefined
  let propStartIndex: number

  if (BaseIcon && segments.length > 1 && BaseIcon[segments[1]]) {
    IconComponent = BaseIcon[segments[1]] as React.ComponentType<
      Record<string, unknown>
    >
    propStartIndex = 2
  } else {
    IconComponent = (LobeIcons as Record<string, unknown>)[baseKey] as
      | React.ComponentType<Record<string, unknown>>
      | undefined
    propStartIndex = segments.length > 1 && /^[A-Z]/.test(segments[1]) ? 2 : 1
  }

  // Fallback if icon not found
  if (
    !IconComponent ||
    (typeof IconComponent !== 'function' && typeof IconComponent !== 'object')
  ) {
    return <LetterBadge name={trimmedName} size={size} />
  }

  // Parse chained properties (e.g., "type={'platform'}", "shape='square'")
  const props: Record<string, string | number | boolean> = {}

  for (let i = propStartIndex; i < segments.length; i++) {
    const seg = segments[i]
    if (!seg) continue

    const eqIdx = seg.indexOf('=')
    if (eqIdx === -1) {
      props[seg.trim()] = true
      continue
    }

    const key = seg.slice(0, eqIdx).trim()
    const valRaw = seg.slice(eqIdx + 1).trim()
    props[key] = parseValue(valRaw)
  }

  // Set size if not explicitly specified in the string
  if (props.size == null && size != null) {
    props.size = size
  }

  return <IconComponent {...props} />
}
